import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { CIBLE_GESTE, type GesteDetail, type Portefeuille } from "./contract";

/**
 * Les demandes « C'est fait » en attente, gardees dans le navigateur.
 *
 * Le 202 du bouton dit que le workflow est parti, pas que le geste est coche : le commit
 * arrive une a deux minutes plus tard, par `docs-sync`. Entre les deux, la page doit se
 * souvenir de la demande pour dire « Demande envoyee a 14:32 » et ne pas reproposer le
 * bouton, y compris apres un rechargement ou un detour par l'accueil. C'est un confort
 * d'affichage et rien d'autre : la verite du geste reste le depot, que la page relit.
 *
 * Trois regles tenues ici :
 *
 * - **le navigateur peut refuser.** Stockage bloque, site sans donnees, navigation privee
 *   stricte : chaque lecture et chaque ecriture passent par un `try`, et la demande vit alors
 *   en memoire jusqu'au rechargement ;
 * - **une demande vit une heure.** Au-dela, le runner est eteint ou la demande s'est perdue :
 *   le bouton revient, et la demande est retiree des qu'un ecran la voit ;
 * - **tout est pur jusqu'a la derniere section**, horloge comprise, passee en argument.
 */

/** La cle du navigateur : `{ [cible]: horodatage ISO de la demande }`. */
export const CLE_DEMANDES = "console-lab:gestes-demandes";

/** Au-dela d'une heure, une demande est oubliee et le bouton revient. */
export const DUREE_DEMANDE_MS = 60 * 60_000;

/**
 * Cinq minutes sans commit : le workflow aurait du aboutir (une a deux minutes d'habitude).
 * lab-tools recoit un 204 de GitHub meme runner eteint, la page ne peut pas le savoir
 * autrement.
 */
export const TOUJOURS_RIEN_MINUTES = 5;

/**
 * Le rythme des relectures tant qu'une demande attend : de quoi voir le commit arriver sans
 * recharger, et pas plus, la couche du Lab comptant chaque lecture comme une visite.
 */
export const RELECTURE_DEMANDE_MS = 15_000;

/** Les demandes en attente : la cible du geste, et l'horodatage ISO de l'envoi. */
export type Demandes = Readonly<Record<string, string>>;

/**
 * Ce que le navigateur a garde, lu sans confiance : un objet dont chaque cle est une cible et
 * chaque valeur une date lisible. Le reste (JSON casse, tableau, valeur inattendue, cle qui
 * n'a pas la forme d'une cible) compte pour rien.
 */
export function analyserDemandes(brut: string | null): Demandes {
  if (brut === null) return {};
  let valeur: unknown;
  try {
    valeur = JSON.parse(brut);
  } catch {
    return {};
  }
  if (typeof valeur !== "object" || valeur === null || Array.isArray(valeur)) return {};
  const demandes: Record<string, string> = {};
  for (const [cible, demandeLe] of Object.entries(valeur)) {
    if (
      CIBLE_GESTE.test(cible) &&
      typeof demandeLe === "string" &&
      !Number.isNaN(Date.parse(demandeLe))
    ) {
      demandes[cible] = demandeLe;
    }
  }
  return demandes;
}

/**
 * Une demande encore valable : moins d'une heure d'ecart avec l'horloge, dans un sens comme
 * dans l'autre. Une date loin dans le futur (horloge fausse, valeur trafiquee) ne doit pas
 * cacher le bouton pour toujours.
 */
export function demandeValable(demandeLe: string, maintenant: number): boolean {
  return Math.abs(maintenant - Date.parse(demandeLe)) < DUREE_DEMANDE_MS;
}

/** Les demandes encore valables a `maintenant`. */
export function demandesValables(demandes: Demandes, maintenant: number): Demandes {
  return Object.fromEntries(
    Object.entries(demandes).filter(([, demandeLe]) => demandeValable(demandeLe, maintenant)),
  );
}

/** L'horodatage de la demande qui attend pour cette cible, ou null. */
export function demandeEnAttente(
  demandes: Demandes,
  cible: string,
  maintenant: number,
): string | null {
  const demandeLe = Object.hasOwn(demandes, cible) ? demandes[cible] : undefined;
  return demandeLe !== undefined && demandeValable(demandeLe, maintenant) ? demandeLe : null;
}

/**
 * Les minutes entieres ecoulees depuis la demande. Jamais negatives : l'horodatage vient du
 * serveur, et une horloge du navigateur un peu en retard ne doit pas donner « dans 1 min ».
 */
export function minutesDepuis(demandeLe: string, maintenant: number): number {
  return Math.max(0, Math.floor((maintenant - Date.parse(demandeLe)) / 60_000));
}

/** Rien apres cinq minutes : le moment de soupconner le runner. */
export function toujoursRien(demandeLe: string, maintenant: number): boolean {
  return minutesDepuis(demandeLe, maintenant) >= TOUJOURS_RIEN_MINUTES;
}

/** « 14:32 » : l'heure de la demande, a l'heure du navigateur. */
export function heureDeDemande(demandeLe: string): string {
  return new Date(demandeLe).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

/** Le rythme de relecture d'un ecran : tant qu'une des cibles qu'il montre attend son commit. */
export function relectureSiDemande(
  cibles: readonly (string | null)[],
  demandes: Demandes,
  maintenant: number,
): number | false {
  const attend = cibles.some(
    (cible) => cible !== null && demandeEnAttente(demandes, cible, maintenant) !== null,
  );
  return attend ? RELECTURE_DEMANDE_MS : false;
}

/** La page d'un guide se relit tant que son geste, pas encore fait, attend un commit. */
export function relectureDuGeste(
  lu: GesteDetail | undefined,
  demandes: Demandes,
  maintenant: number,
): number | false {
  if (lu === undefined || lu.geste.fait) return false;
  return relectureSiDemande([lu.geste.cible], demandes, maintenant);
}

/** L'accueil se relit tant qu'un des gestes qu'il liste attend un commit. */
export function relectureDuPortefeuille(
  lu: Portefeuille | undefined,
  demandes: Demandes,
  maintenant: number,
): number | false {
  if (lu === undefined) return false;
  return relectureSiDemande(
    lu.prerequis.map((geste) => geste.cible),
    demandes,
    maintenant,
  );
}

// ---------------------------------------------------------------------------------------
// Le navigateur. La seule partie qui ne soit pas pure : `localStorage`, et sa copie en
// memoire pour le jour ou il refuse.

/**
 * La derniere ecriture de cette session. Elle sert quand le navigateur refuse de lire, ou
 * qu'il a refuse la derniere ecriture : la demande tient alors jusqu'au rechargement de la
 * page, pas au-dela.
 */
let memoire: string | null = null;

/**
 * Vrai quand le navigateur a refuse la derniere ecriture (quota plein, stockage en lecture
 * seule) alors qu'il sait encore lire : ce qu'il garde est en retard sur la page, et relu tel
 * quel il ferait oublier la demande qui vient de partir. La memoire fait foi jusqu'a la
 * prochaine ecriture reussie.
 */
let ecritureRefusee = false;

/** Les ecrans qui lisent les demandes, prevenus a chaque ecriture. */
const abonnes = new Set<() => void>();

function lireBrut(): string | null {
  if (ecritureRefusee) return memoire;
  try {
    return window.localStorage.getItem(CLE_DEMANDES);
  } catch {
    return memoire;
  }
}

function prevenirLesEcrans(): void {
  for (const prevenir of abonnes) prevenir();
}

function ecrire(demandes: Demandes): void {
  const json = Object.keys(demandes).length === 0 ? null : JSON.stringify(demandes);
  memoire = json;
  try {
    if (json === null) window.localStorage.removeItem(CLE_DEMANDES);
    else window.localStorage.setItem(CLE_DEMANDES, json);
    ecritureRefusee = false;
  } catch {
    // Le navigateur ne garde rien : la memoire suffit jusqu'au rechargement.
    ecritureRefusee = true;
  }
  prevenirLesEcrans();
}

/**
 * Un autre onglet a ecrit les demandes, ou tout efface (cle nulle) : les ecrans de celui-ci se
 * relisent. Sans cela, un clic dans un onglet laisserait l'autre proposer encore le bouton.
 * Le navigateur ne previent jamais l'onglet qui ecrit : celui-la passe par `ecrire`.
 */
function surStockage(evenement: StorageEvent): void {
  if (evenement.key === CLE_DEMANDES || evenement.key === null) prevenirLesEcrans();
}

function abonner(prevenir: () => void): () => void {
  if (abonnes.size === 0) window.addEventListener("storage", surStockage);
  abonnes.add(prevenir);
  return () => {
    abonnes.delete(prevenir);
    if (abonnes.size === 0) window.removeEventListener("storage", surStockage);
  };
}

/**
 * Uniquement pour les tests : oublie la memoire et le dernier refus d'ecriture, qui
 * survivraient sinon d'un test a l'autre.
 */
export function oublierLaMemoire(): void {
  memoire = null;
  ecritureRefusee = false;
}

/** Les demandes gardees par le navigateur, telles quelles, perimees comprises. */
export function lireDemandes(): Demandes {
  return analyserDemandes(lireBrut());
}

/**
 * Note la demande qui vient de partir. L'horodatage est celui du serveur ; s'il est illisible
 * ou a plus d'une heure de l'horloge du navigateur, c'est celle-ci qui compte : une demande qui
 * vient de partir doit se voir, pas etre oubliee aussitot.
 */
export function noterDemande(cible: string, demandeLe: string, maintenant: number): void {
  const quand = demandeValable(demandeLe, maintenant)
    ? demandeLe
    : new Date(maintenant).toISOString();
  ecrire({ ...demandesValables(lireDemandes(), maintenant), [cible]: quand });
}

/** Oublie la demande d'un geste, quand la page le voit fait. */
export function oublierDemande(cible: string): void {
  const lues = lireDemandes();
  if (!Object.hasOwn(lues, cible)) return;
  ecrire(Object.fromEntries(Object.entries(lues).filter(([autre]) => autre !== cible)));
}

/**
 * Retire les demandes perimees et, quand la liste des cibles encore attendues est donnee,
 * celles qui n'y sont plus : leur geste est fait, ou n'existe plus. N'ecrit que si quelque
 * chose part, pour ne pas reveiller les ecrans pour rien.
 */
export function nettoyerDemandes(maintenant: number, attendues?: readonly string[]): void {
  const lues = lireDemandes();
  const gardees = Object.fromEntries(
    Object.entries(lues).filter(
      ([cible, demandeLe]) =>
        demandeValable(demandeLe, maintenant) &&
        (attendues === undefined || attendues.includes(cible)),
    ),
  );
  if (Object.keys(gardees).length !== Object.keys(lues).length) ecrire(gardees);
}

/**
 * Les demandes encore valables a `maintenant`, relues a chaque ecriture de la page. Les
 * perimees sont retirees du navigateur des qu'un ecran les voit.
 */
export function useDemandes(maintenant: number): Demandes {
  const brut = useSyncExternalStore(abonner, lireBrut);
  const toutes = useMemo(() => analyserDemandes(brut), [brut]);
  const valables = useMemo(() => demandesValables(toutes, maintenant), [toutes, maintenant]);
  const perimees = Object.keys(valables).length < Object.keys(toutes).length;
  useEffect(() => {
    if (perimees) nettoyerDemandes(Date.now());
  }, [perimees]);
  return valables;
}

/**
 * Rend l'ecran toutes les `intervalle` ms tant que `actif` : ce qu'il montre d'une demande se
 * lit sur l'horloge (« toujours rien » a cinq minutes, l'oubli a une heure), et une relecture
 * qui rend les memes donnees ne suffit pas a le rendre. Il vit ici parce que la carte du
 * bouton et l'accueil s'en servent, et qu'aucun des deux n'importe l'autre.
 */
export function useTicTac(intervalle: number, actif: boolean): void {
  const [, setTic] = useState(0);
  useEffect(() => {
    if (!actif) return;
    const minuterie = setInterval(() => setTic((tic) => tic + 1), intervalle);
    return () => clearInterval(minuterie);
  }, [actif, intervalle]);
}
